import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardRequisitionColumnEnum, Requisition, RequisitionProgressEnum, SelectableRequisition, UsersWho } from '../../models/requisition.model';

@Component({
  selector: 'app-dashboard-requisition-table',
  templateUrl: './dashboard-requisition-table.component.html',
  styleUrl: './dashboard-requisition-table.component.scss'
})
export class DashboardRequisitionTableComponent {
  @Input() requisitionTableData: Requisition[] = [];
  @Input()totalRequisitions: number = 0;
  @Input() usersInRequisitions: UsersWho = {
    userWhoAuthorizes: [],
    userWhoBuys: [],
    userWhoReviews: [],
    userWhoRequests: [],
  };
  @Input() userPermissions?: {
    canViewAmounts?: boolean;
    isAdmin?: boolean;
  };
  @Input() requisitionTabsList: string[] = [];
  @Output() highlightRequisition = new EventEmitter<{
    requisition: Requisition;
    tableIndex: number;
  }>();

  currentTab: number = 0;

  requisitionTableDataBackup: Requisition[] = [];
  loading: boolean = false;
  pageSize: number = 10;
  openedNotes = new Set<number>();
  extraActionButtonText: string = 'Ir a mis requisiciones';
  paginatorItems: number[] = [];
  itemsPerPage: number[] = [10, 20, 30];
  currentPage: number = 1;
  
  public requisitionColumnsEnum = DashboardRequisitionColumnEnum;
  currentRowElements: Partial<SelectableRequisition>[] = [];
  requisitionColumnsList = [
    {
      title: 'Solicita',
      name: 'userWhoRequests',
      width: '350px',
      align: 'left',
      isVisible: true,
    },
    {
      title: 'Folio',
      name: 'requisitionFolio',
      width: '120px',
      align: 'left',
      isVisible: true,
    },
    {
      title: 'Descripción',
      name: 'requisitionDescription',
      width: '350px',
      align: 'left',
      isVisible: true,
    },
    {
      title: 'Proyecto',
      name: 'requisitionProject',
      width: '350px',
      align: 'left',
      isVisible: true,
    },
    {
      title: 'Fecha de solicitud',
      name: 'requisitionApplicationDate',
      width: '200px',
      align: 'center',
      isVisible: true,
    },
    {
      title: 'Entrega esperada',
      name: 'requisitionExpectedDeliveryDate',
      width: '200px',
      align: 'center',
      isVisible: true,
    },
    {
      title: 'Monto requisitado',
      name: 'totalCost',
      width: '180px',
      align: 'center',
      isVisible: false,
    },
    {
      title: 'Autoriza',
      name: 'userWhoAuthorizes',
      width: '350px',
      align: 'left',
      isVisible: true,
    },
    {
      title: 'Prioridad',
      name: 'requisitionPriority',
      width: '140px',
      align: 'center',
      isVisible: true,
    },
    {
      title: 'Progreso Actual',
      name: 'requisitionProgress',
      width: '200px',
      align: 'center',
      isVisible: true,
    },
  ];

  constructor(private router: Router, private clipboard: Clipboard) {}

  ngOnChanges(): void {
    this.paginatorItems =
      this.totalRequisitions / this.pageSize
        ? [
            ...Array(Math.ceil(this.totalRequisitions / this.pageSize)).keys(),
          ].map((x) => x + 1)
        : [];
    this.requisitionTableDataBackup = [...this.requisitionTableData];
    this.currentRowElements = this.requisitionTableData.map((x) => ({
      requisitionId: x.requisitionId,
      isFolioTooltipVisible: false
    }));
    this.requisitionColumnsList[this.requisitionColumnsEnum.TotalCost].isVisible = this.userPermissions?.canViewAmounts ?? false;
  }

  onChangeRequisitionTab(tab: number): void {
    this.currentTab = tab;
    switch (tab) {
      case 0:
        // Todas
        this.requisitionTableData = [...this.requisitionTableDataBackup];
        break;
      case 1:
        // Iniciado
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum.Iniciado);
        break;
      case 2:
        // En espera de revisión
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum['En espera de revisión']);
        break;
      case 3:
        // En espera de autorizar
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum['En espera de autorizar']);
        break;
      case 4:
        // Enviado a compras
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum['Enviado a compras']);
        break;
      case 5:
        // En proceso de compra
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum['En proceso de compra']);
        break;
      case 6:
        // Comprada
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum.Comprada);
        break;
      case 7:
        // En proceso de entrega
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum['En proceso de entrega']);
        break;
      case 8:
        // Vencida
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum.Vencida);
        break;
      case 9:
        // Entregada
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum.Entregada);
        break;
      case 10:
        // Rechazada
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum.Rechazada);
        break;
      case 11:
        // Cancelada
        this.requisitionTableData = this.requisitionTableDataBackup
          .filter((x) => x.requisitionProgress?.id === RequisitionProgressEnum.Cancelada);
        break;
      default:
        this.requisitionTableData = [...this.requisitionTableDataBackup];
        break;
    }
  }

  onCopyToClipboard(value: string, index: number, $event: any) {
    $event.stopPropagation();
    $event.preventDefault();
    if (value === '') return
    this.clipboard.copy(value);
    this.currentRowElements[index].isFolioTooltipVisible = true;

    setTimeout(() => {
      this.currentRowElements[index].isFolioTooltipVisible = false;
    }, 1000);
  }

  onHighlightRequisition(
    data: Requisition,
    isHighlighted: boolean,
    tableIndex: number,
    $event: any
  ) {
    $event.stopPropagation();
    $event.preventDefault();
    this.highlightRequisition.emit({
      requisition: { ...data, requisitionIsHighlighted: isHighlighted },
      tableIndex: tableIndex,
    });
  }

  onSelectNote(id: number, $event: any) {
    $event.stopPropagation();
    $event.preventDefault();
    this.openedNotes.has(id) ? this.openedNotes.delete(id) : this.openedNotes.add(id);
  }

  onCloseNote(id: number) {
    this.openedNotes.delete(id);
  }

  onGoToRequisitionDetails(requisition: Requisition) {
    this.router.navigate([
      `requisiciones/${requisition.project.projectId ?? 'all'}/requisicion/${requisition.requisitionId}`,
    ]);
  }
}
